/**
 * Reads the `BTN_*` channel family for the BUTTONS pages.
 *
 * `BTN_*` exists in InfluxDB ONLY — it is in no normalization parquet and the live stream does not subscribe
 * to it (nothing in this repo even names a button channel). So both paths here have to reach Influx, and
 * neither can take the shortcut the Alarms pages take of reading a parquet family straight off disk.
 *
 * TWO PATHS, ONE SHAPE. Live reads the stream server's rolling window; dataset/day reads a window around
 * the playhead from the same endpoint. Both normalise to `ButtonData`.
 *
 * The first read of a window comes from Timescale or Influx, and the stream server writes those state
 * changes to `btn_*.parquet` under the dataset day. The next read of a window those files cover comes
 * back from the file. Pass project, class and date so the file lands in the same folder a later
 * channel-values request will look in.
 */

import { apiEndpoints } from '@config/env';
import { getData } from '@utils/global';
import { warn, debug } from '@utils/console';

/** Every button channel starts with this. Must match liveButtons.js BUTTON_CHANNEL_PREFIX. */
export const BUTTON_CHANNEL_PREFIX = 'BTN_';

/** Sparkline span. The live poll asks for a little more than this so a late bucket cannot short the window. */
export const SPARKLINE_WINDOW_MS = 20_000;

/**
 * One load's worth of button data, column-oriented.
 *
 * `ts` is epoch MILLISECONDS ascending and shared by every channel; each `values` entry is aligned to it, with
 * `NaN` meaning "no sample in this slot" — which the page renders as an unlit LED with no sparkline segment,
 * distinct from a real 0.
 */
export interface ButtonData {
  ts: Float64Array;
  /** Every channel to show a row for, including ones that never changed. Sorted. */
  channels: string[];
  values: Map<string, Float64Array>;
}

export const EMPTY_BUTTON_DATA: ButtonData = {
  ts: new Float64Array(0),
  channels: [],
  values: new Map(),
};

function isButtonChannel(name: unknown): boolean {
  return typeof name === 'string' && name.toUpperCase().startsWith(BUTTON_CHANNEL_PREFIX);
}

function num(v: unknown): number {
  if (v === undefined || v === null || v === '') {return NaN;}
  const n = typeof v === 'bigint' ? Number(v) : Number(v);
  return Number.isFinite(n) ? n : NaN;
}

/* ---- cursor lookup ------------------------------------------------------------------------- */

/**
 * Index of the last sample at or before `cursorMs`, or -1.
 *
 * Buttons are STEP signals: between samples the value holds, it does not interpolate and it does not reset.
 * So the LED at time t is the last sample at or before t, however long ago that was.
 */
export function indexAtOrBefore(ts: Float64Array, cursorMs: number): number {
  if (ts.length === 0 || !Number.isFinite(cursorMs) || cursorMs < ts[0]) {return -1;}
  let lo = 0;
  let hi = ts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ts[mid] <= cursorMs) {lo = mid;} else {hi = mid - 1;}
  }
  return lo;
}

/**
 * Value of one channel at the cursor, holding the last real sample forwards.
 *
 * Walks back over `NaN` slots: a channel that reports once a minute is still ON between its samples, and
 * returning NaN there would blink its LED off for 59 of every 60 seconds. Returns null when there is no
 * sample at all at or before the cursor — genuinely "no data yet", which renders as the unfilled circle.
 */
export function valueAt(data: ButtonData, channel: string, cursorMs: number): number | null {
  const values = data.values.get(channel);
  if (!values) {return null;}
  let i = indexAtOrBefore(data.ts, cursorMs);
  while (i >= 0) {
    const v = values[i];
    if (Number.isFinite(v)) {return v;}
    i -= 1;
  }
  return null;
}

/** A press held this long is suspicious - the LED turns yellow. */
export const ENGAGED_WARN_MS = 20_000;
/** A press held this long is almost certainly stuck - the LED turns red. */
export const ENGAGED_ALERT_MS = 60_000;

/**
 * How long a channel has been continuously engaged at the cursor, or null if it is not engaged.
 *
 * A button held down for a long time is either a jammed switch or someone leaning on it, and neither is
 * visible from the instantaneous value - which is why this exists rather than colouring on `value` alone.
 *
 * NaN slots do NOT end a run. A gap in the feed is a gap, not a release: treating it as one would restart
 * the timer every time a sample went missing and a genuinely stuck button would never reach the threshold.
 *
 * LOWER BOUND, not an exact age. The page only holds a 15 minute window, so a press that began before the
 * window started is measured from the first sample in it. That is safe for thresholds - a run already past
 * 60s is still past 60s however much earlier it truly began - but the number should not be shown as an
 * exact "held for" figure without that caveat.
 */
export function engagedForMs(data: ButtonData, channel: string, cursorMs: number): number | null {
  const values = data.values.get(channel);
  if (!values || data.ts.length === 0 || !Number.isFinite(cursorMs)) {return null;}

  // The value in force at the cursor, skipping back over any gap (same hold-last rule as valueAt).
  let i = indexAtOrBefore(data.ts, cursorMs);
  while (i >= 0 && !Number.isFinite(values[i])) {i -= 1;}
  if (i < 0 || !(values[i] > 0)) {return null;}

  // Walk back to the first sample of this engaged run. A finite <= 0 ends it; NaN is skipped.
  let startIdx = i;
  for (let j = i - 1; j >= 0; j -= 1) {
    const v = values[j];
    if (!Number.isFinite(v)) {continue;}
    if (v > 0) {
      startIdx = j;
      continue;
    }
    break;
  }
  return Math.max(0, cursorMs - data.ts[startIdx]);
}

/**
 * Timestamp of the most recent moment this channel was ON at or before the cursor, or null if it was
 * never on in the loaded window.
 *
 * "Last ON", not "last 0->1 transition". For a button still held, that means the cursor itself, so a
 * currently-engaged button sorts to the very top of a recency list rather than behind a shorter press
 * that started later. Onset would invert that, which is not what "most recently pressed" should mean.
 *
 * A held button may have only one stored sample — the second it changed to on. Timescale returns state
 * changes, not a 1 on every second the press continues, so the cursor stands in for that missing run of
 * 1s. A released button still reports the last stored on-sample.
 *
 * Searches the whole loaded window, not just the sparkline span: rows are SELECTED over the whole
 * 15 minute window, so ordering them by a 20s view would leave most of them tied at "never".
 */
export function lastPressedMs(data: ButtonData, channel: string, cursorMs: number): number | null {
  const values = data.values.get(channel);
  if (!values || data.ts.length === 0 || !Number.isFinite(cursorMs)) {return null;}
  let i = indexAtOrBefore(data.ts, cursorMs);
  while (i >= 0 && !Number.isFinite(values[i])) {i -= 1;}
  if (i >= 0 && values[i] > 0) {return cursorMs;}
  for (; i >= 0; i -= 1) {
    const v = values[i];
    if (Number.isFinite(v) && v > 0) {return data.ts[i];}
  }
  return null;
}

/** LED state at the cursor. `off` covers both a sampled 0 and no sample at all. */
export type ButtonLedState = 'off' | 'on' | 'warn' | 'alert';

/**
 * Engaged presses are graded by HOW LONG they have been held, not by their value.
 *
 * This supersedes an earlier rule where yellow meant "above zero but not exactly 1". Production `BTN_`
 * values are strictly 0 or 1 (verified against Influx), so that state was unreachable, and the duration
 * grading is what actually tells you something - a stuck button.
 */
export function ledStateFor(data: ButtonData, channel: string, cursorMs: number): ButtonLedState {
  const heldMs = engagedForMs(data, channel, cursorMs);
  if (heldMs === null) {return 'off';}
  if (heldMs > ENGAGED_ALERT_MS) {return 'alert';}
  if (heldMs > ENGAGED_WARN_MS) {return 'warn';}
  return 'on';
}

/**
 * The `[cursor - windowMs, cursor]` slice of one channel, for the sparkline.
 * Returns points in order; `NaN` values are kept so the renderer can break the line over a real gap.
 *
 * The value in force at the left edge is included even when its sample is older than the window.
 * Timescale sends the second a button changed, not every second it stayed on, and a press that began
 * before these 20s would otherwise draw as an empty trace while the LED is lit.
 */
export function windowSlice(
  data: ButtonData,
  channel: string,
  cursorMs: number,
  windowMs: number = SPARKLINE_WINDOW_MS
): { ts: number[]; values: number[] } {
  const out = { ts: [] as number[], values: [] as number[] };
  const series = data.values.get(channel);
  if (!series || data.ts.length === 0 || !Number.isFinite(cursorMs) || windowMs <= 0) {return out;}
  const startMs = cursorMs - windowMs;
  let holdIdx = indexAtOrBefore(data.ts, startMs);
  while (holdIdx >= 0 && !Number.isFinite(series[holdIdx])) {holdIdx -= 1;}
  // A sample that already sits on the left edge is emitted by the loop below. Prepending as well
  // would draw that instant twice.
  if (holdIdx >= 0 && data.ts[holdIdx] < startMs) {
    out.ts.push(startMs);
    out.values.push(series[holdIdx]);
  }
  for (let i = 0; i < data.ts.length; i += 1) {
    const t = data.ts[i];
    if (t < startMs) {continue;}
    if (t > cursorMs) {break;}
    out.ts.push(t);
    out.values.push(series[i]);
  }
  return out;
}

/* ---- dataset / day ------------------------------------------------------------------------- */

/**
 * How much history one windowed read covers, and how much slack before it re-reads.
 *
 * WHY A WINDOW AND NOT THE WHOLE DAY. BTN_ is logged at 20Hz: one channel, one boat, one day is ~537,000
 * raw points, and a boat carries ~114 of them - about 61 MILLION points for a day. Asking channel-values
 * for that 504s every time, which is exactly what made the page render a full channel list with nothing
 * behind it. The same family over a few minutes, bucketed to 1s, is ~200ms measured against production.
 *
 * So the page reads a window around the playhead and re-reads when the playhead approaches an edge.
 *
 * 13 min behind + 2 min ahead = 15 min, which is EXACTLY the server's default
 * STREAM_BUTTONS_RANGE_MAX_MS. That is deliberate but tight: the server clamps with
 * `Math.min(requested, cap)` and silently shortens anything wider, so widening these constants without
 * also raising that env var would quietly give you less history than you asked for, not an error.
 *
 * The width matters for more than caching. The server also SELECTS the rows over the 15 minutes before
 * the playhead: a channel earns a row by going above zero in that span, while the sparkline only shows
 * the last 20s. That gap is intentional - it keeps a button visible in context for a while after its
 * press rather than flashing in and out of the table as the playhead moves.
 */
export const RANGE_BEFORE_MS = 780_000;
export const RANGE_AFTER_MS = 120_000;
/** Re-read once the cursor comes within this of either edge, so a small scrub does not refetch. */
export const RANGE_EDGE_SLACK_MS = 30_000;

export interface ButtonRange {
  startMs: number;
  endMs: number;
}

/** The window to load for a cursor. */
export function rangeForCursor(cursorMs: number): ButtonRange {
  return { startMs: cursorMs - RANGE_BEFORE_MS, endMs: cursorMs + RANGE_AFTER_MS };
}

/** True when `cursorMs` is far enough inside `loaded` that the held data still covers the sparkline. */
export function rangeCovers(loaded: ButtonRange | null, cursorMs: number): boolean {
  if (!loaded || !Number.isFinite(cursorMs)) {return false;}
  return (
    cursorMs >= loaded.startMs + SPARKLINE_WINDOW_MS &&
    cursorMs <= loaded.endMs - RANGE_EDGE_SLACK_MS
  );
}

export interface ButtonCacheContext {
  projectId?: number | string;
  className?: string;
  dateYmd?: string;
}

function cacheQuery(ctx?: ButtonCacheContext): string {
  if (!ctx) {return '';}
  const parts: string[] = [];
  if (ctx.projectId != null && ctx.projectId !== '') {
    parts.push(`project_id=${encodeURIComponent(String(ctx.projectId))}`);
  }
  if (ctx.className) {parts.push(`class_name=${encodeURIComponent(ctx.className)}`);}
  if (ctx.dateYmd) {parts.push(`date=${encodeURIComponent(ctx.dateYmd)}`);}
  return parts.length > 0 ? `&${parts.join('&')}` : '';
}

export interface FetchButtonsArgs extends ButtonCacheContext {
  sourceName: string;
  startMs: number;
  endMs: number;
  /**
   * The playhead. Bounds the active-channel selection to the 15 minutes BEFORE it, so the row list means
   * the same thing here as on the live page and a replay never lists a press from its own future.
   */
  cursorMs?: number;
  signal?: AbortSignal;
}

/**
 * Button values for one boat over one window, from the stream server's on-demand Influx query.
 *
 * A window, not the whole day: one channel at 20Hz is ~537,000 points and a boat has ~114 of them.
 * The stream server reads the state changes, writes them to parquet, and serves the same window from
 * that file the next time.
 *
 * The channel list comes back with the data, so there is no separate discovery call.
 */
export async function fetchButtonRange(args: FetchButtonsArgs): Promise<LiveButtonsPayload> {
  const { sourceName, startMs, endMs, cursorMs, signal } = args;
  if (!sourceName || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {return EMPTY_LIVE_BUTTONS;}

  const url =
    `${apiEndpoints.stream.buttonsLive}` +
    `?start_ms=${Math.round(startMs)}&end_ms=${Math.round(endMs)}` +
    `&source_name=${encodeURIComponent(sourceName)}` +
    (Number.isFinite(cursorMs) ? `&cursor_ms=${Math.round(cursorMs as number)}` : '') +
    cacheQuery(args);

  try {
    const resp = await getData(url, signal);
    if (!resp?.success || !resp.data) {
      if (!signal?.aborted) {
        warn('[buttons] range read failed', { sourceName, startMs, endMs, status: resp?.status });
      }
      return EMPTY_LIVE_BUTTONS;
    }
    return parseButtonsPayload(resp.data);
  } catch (e) {
    debug('[buttons] range read threw', e);
    return EMPTY_LIVE_BUTTONS;
  }
}

/* ---- live --------------------------------------------------------------------------------- */

export interface LiveButtonsPayload extends ButtonData {
  /** Boats the stream server has seen buttons from, for the source dropdown. */
  sources: string[];
}

export const EMPTY_LIVE_BUTTONS: LiveButtonsPayload = { ...EMPTY_BUTTON_DATA, sources: [] };

interface RawLiveTimestep {
  DATETIME?: string;
  SOURCE?: string;
  BUTTONS?: { NAME?: string; VALUE?: string }[];
}

/**
 * One live poll.
 *
 * Asks for a trailing window a little wider than the sparkline so a bucket that lands late still appears, and
 * for one boat only — the response is otherwise (sources x buckets x buttons) and nobody looks at more than
 * one boat at a time. `sources` and `channels` still come back whole, because the dropdown and the row list
 * are not derivable from a one-boat slice.
 *
 * Never throws: a failed poll leaves the page showing its previous snapshot rather than blanking it.
 */
export async function fetchLiveButtons(opts?: {
  sourceName?: string;
  windowMs?: number;
  signal?: AbortSignal;
} & ButtonCacheContext): Promise<LiveButtonsPayload> {
  const windowMs = opts?.windowMs ?? SPARKLINE_WINDOW_MS * 1.5;
  const sinceMs = Date.now() - windowMs;

  let url = `${apiEndpoints.stream.buttonsLive}?since_ms=${Math.round(sinceMs)}`;
  if (opts?.sourceName) {url += `&source_name=${encodeURIComponent(opts.sourceName)}`;}
  url += cacheQuery(opts);


  try {
    const resp = await getData(url, opts?.signal);
    if (!resp?.success || !resp.data) {return EMPTY_LIVE_BUTTONS;}
    return parseButtonsPayload(resp.data);
  } catch (e) {
    debug('[buttons] live fetch failed', e);
    return EMPTY_LIVE_BUTTONS;
  }
}

/** Both the live poll and the windowed range read return the same envelope; parse it in one place. */
export function parseButtonsPayload(raw: unknown): LiveButtonsPayload {
  const payload = (raw ?? {}) as { sources?: unknown; channels?: unknown; timesteps?: RawLiveTimestep[] };
  return liveTimestepsToButtonData(
    Array.isArray(payload.timesteps) ? payload.timesteps : [],
    Array.isArray(payload.channels) ? (payload.channels.filter(isButtonChannel) as string[]) : [],
    Array.isArray(payload.sources) ? (payload.sources.filter((s) => typeof s === 'string') as string[]) : []
  );
}

/** Convert the stream server's per-bucket timesteps into the same column shape the day path produces. */
export function liveTimestepsToButtonData(
  timesteps: RawLiveTimestep[],
  channels: string[],
  sources: string[]
): LiveButtonsPayload {
  // One column per bucket. The caller has already narrowed to a single boat, but merge by DATETIME anyway so
  // an unfiltered response cannot produce two columns with the same timestamp.
  const byMs = new Map<number, Map<string, number>>();
  const seen = new Set<string>(channels);

  for (const ts of timesteps) {
    const ms = ts.DATETIME ? new Date(ts.DATETIME).getTime() : NaN;
    if (!Number.isFinite(ms)) {continue;}
    let slot = byMs.get(ms);
    if (!slot) {
      slot = new Map();
      byMs.set(ms, slot);
    }
    for (const b of ts.BUTTONS ?? []) {
      if (!isButtonChannel(b?.NAME)) {continue;}
      const name = b.NAME as string;
      const v = num(b.VALUE);
      if (!Number.isFinite(v)) {continue;}
      seen.add(name);
      slot.set(name, v);
    }
  }

  const orderedMs = [...byMs.keys()].sort((a, b) => a - b);
  const allChannels = [...seen].sort();
  const tsArr = new Float64Array(orderedMs.length);
  const values = new Map<string, Float64Array>();
  for (const ch of allChannels) {values.set(ch, new Float64Array(orderedMs.length).fill(NaN));}

  for (let i = 0; i < orderedMs.length; i += 1) {
    tsArr[i] = orderedMs[i];
    const slot = byMs.get(orderedMs[i]);
    if (!slot) {continue;}
    for (const [name, v] of slot) {
      const arr = values.get(name);
      if (arr) {arr[i] = v;}
    }
  }

  return { ts: tsArr, channels: allChannels, values, sources: [...sources].sort() };
}
